import React, {
  useEffect,
  useState,
  forwardRef,
  useCallback,
  useMemo,
} from "react";
import { Send } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppContext } from "../../../context/AppContext";
import useLocalization from "../../../hooks/useLocalization";
import { KpmgTransliterate } from "../TranslitrationSuggestion/KpmgTransliterate";
import LanguageModal from "../LanguageModel/LanguageModal";
import { useLogger } from "../../../utils/UnifiedLogger";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";

import {
  selectInputValue,
  selectInputPlaceholder,
  selectIsInputDisabled as selectIsInputBarDisabled,
  selectSendButtonDisabled,
} from "../../../store/features/ui/uiSelectors";

import {
  setInputValue,
  setInputPlaceholder,
} from "../../../store/features/ui/uiSlice";

import {
  selectIsTextLanguageDetecting,
  selectIsContentGenerating,
  selectIsInputDisabled,
  selectSelectedChatId,
  selectIsVoiceFlow,
  selectIsSuggestedQuestionsVisible,
  selectIsVoiceFlowLocked,
  selectIsSocketRecording,
  selectIsNotRelatedToCenter,
  selectIsGrievanceStatusFlowEnded,
} from "../../../store/features/message/textContentSelectors";
import { updateFlowStates } from "../../../store/features/message/updateFlowStates";
import { getChatConfig } from "../../../utils/chatConfigStorage";
import { setAccumulatedFinalTranscript, setCurrentInterimTranscript } from "../../../store/features/message/textContentSlice";

const UserInputBarWithSendOnly = forwardRef(
  ({ onChange, onKeyDown, handleSend, isTypingRef }, ref) => {
    const { logInfo } = useLogger("UserInputBarWithSendOnly", false);
    const { t } = useLocalization();
    const { selectedLanguageData } = useAppContext();
    const dispatch = useDispatch();
    let chatConfig = getChatConfig();

    // Language Modal State
    const [showLanguages, setShowLanguages] = useState(false);

    const isLanguageDetecting = useSelector(selectIsTextLanguageDetecting);
    const isContentGenerating = useSelector(selectIsContentGenerating);
    const isRecording = useSelector(selectIsSocketRecording);
    const isInputDisabled = useSelector(selectIsInputDisabled);
    const isInputBarDisabled = useSelector(selectIsInputBarDisabled);
    const isSendButtonDisabled = useSelector(selectSendButtonDisabled);
    const isNotRelatedToCenterFlag = useSelector(selectIsNotRelatedToCenter);
    const isGrievanceStatusFlowEnded = useSelector(
      selectIsGrievanceStatusFlowEnded
    );
    // Follow-ups are blocked for state grievances and for ended status flows
    const isNotRelatedToCenter =
      isNotRelatedToCenterFlag || isGrievanceStatusFlowEnded;

    const inputValue = useSelector(selectInputValue);
    const inputPlaceholder = useSelector(selectInputPlaceholder);
    const selectedChatId = useSelector(selectSelectedChatId);
    const isVoiceFlow = useSelector(selectIsVoiceFlow);
    const isVoiceFlowLocked = useSelector(
      selectIsVoiceFlowLocked(selectedChatId)
    );
    const isSuggestedQuestionsVisible = useSelector(
      selectIsSuggestedQuestionsVisible
    );

    const shouldShowTransliterate = useMemo(
      () => selectedLanguageData?.code !== "en",
      [selectedLanguageData?.code]
    );

    const handleTextChange = useCallback(
      (value) => {
        if (chatConfig?.journey === "VOICE") {
          dispatch(setAccumulatedFinalTranscript(value));
          dispatch(setCurrentInterimTranscript(""));
        } else {
          dispatch(setInputValue(value));
        }
        dispatch(updateFlowStates());
      },
      [inputValue]
    );

    useEffect(() => {
      if (selectedLanguageData) {
        dispatch(setInputPlaceholder(t("inputPlaceholder")));
      }
    }, [selectedLanguageData, t]);

    const handleInputChange = useCallback(
      (e) => {
        onChange?.(e);
        handleTextChange(e.target.value);
      },
      [onChange, handleTextChange]
    );

    const handleInputFocus = useCallback(() => {
      isTypingRef.current = true;
      setShowLanguages(true);
    }, [inputPlaceholder]);

    logInfo("RENDERING_USER_INPUT_BAR", "Rendering UserInputBarWithSendOnly", {
      isSuggestedQuestionsVisible,
      isVoiceFlow,
      isVoiceFlowLocked,
      selectedChatId,
    });

    // console.log("Rendering UserInputBarWithSendOnly", {
    //     isSuggestedQuestionsVisible, isVoiceFlow, isVoiceFlowLocked, selectedChatId, isInputBarDisabled, isInputDisabled, isLanguageDetecting, isContentGenerating, isRecording, isSendButtonDisabled
    // });

    const isInputDisabledUI = useMemo(
      () =>
        isInputBarDisabled ||
        isInputDisabled ||
        isLanguageDetecting ||
        isContentGenerating ||
        isNotRelatedToCenter,
      [
        isInputBarDisabled,
        isInputDisabled,
        isLanguageDetecting,
        isContentGenerating,
        isNotRelatedToCenter,
      ]
    );

    const isSendButtonDisabledUI = useMemo(
      () => isSendButtonDisabled || isInputDisabledUI || isRecording || isNotRelatedToCenter,
      [isInputDisabledUI, isRecording, isSendButtonDisabled, isNotRelatedToCenter]
    );

    // Debug: Log the state values
    console.log("UserInputBarWithSendOnly - State Check:", {
      isNotRelatedToCenter,
      isSendButtonDisabledUI,
      isSendButtonDisabled,
      isInputDisabledUI,
      isRecording,
    });

    // if (
    //   !isSuggestedQuestionsVisible &&
    //   (!isVoiceFlow || !isVoiceFlowLocked) &&
    //   !selectedChatId
    // ) {
    //   return (
    //     <div className="relative w-full mx-auto mt-2 mb-3 flex justify-end">
    //       <motion.div
    //         animate={"active"}
    //         transition={{ duration: 0.3, ease: "easeOut" }}
    //         className="flex items-center justify-end relative"
    //       >
    //         <motion.button
    //           whileHover={{ scale: 1.02 }}
    //           whileTap={{ scale: 0.96 }}
    //           onClick={handleSend}
    //           title="Send"
    //           disabled={isSendButtonDisabledUI}
    //           className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700
    //            text-white rounded-full transition-all duration-300
    //            hover:shadow-lg hover:shadow-blue-500/25
    //            disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed
    //            flex items-center justify-center w-[50px] h-[50px] relative overflow-hidden group"
    //         >
    //           <motion.div
    //             animate={{
    //               x: isSendButtonDisabledUI ? 0 : [-100, 100],
    //             }}
    //             transition={{
    //               duration: 2,
    //               repeat: isSendButtonDisabledUI ? 0 : Infinity,
    //               ease: "easeInOut",
    //             }}
    //             className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 opacity-0 group-hover:opacity-100"
    //           />
    //           <div className="flex items-center space-x-2 relative z-10">
    //             <motion.div
    //               animate={{
    //                 x: isSendButtonDisabledUI ? 0 : [0, 3, 0],
    //                 rotate: isSendButtonDisabledUI ? 0 : [0, 15, -5, 0],
    //               }}
    //               transition={{
    //                 duration: 2.5,
    //                 repeat: isSendButtonDisabledUI ? 0 : Infinity,
    //                 ease: "easeInOut",
    //               }}
    //             >
    //               <Send size={18} />
    //             </motion.div>
    //           </div>
    //         </motion.button>
    //       </motion.div>
    //     </div>
    //   );
    // }

    return (
      <div className="relative w-full mx-auto mt-2 mb-5">
        <motion.div
          animate={"active"}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="flex items-center relative"
        >
          <LanguageModal
            isOpen={showLanguages}
            onClose={() => setShowLanguages(false)}
          />
          <div
            className="flex-1 relative bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 
                      rounded-md border border-1 border-[#B3B3B3] transition-all duration-300 hover:shadow-2xl
                      backdrop-blur-sm mx-4"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key="input"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="relative"
              >
                <KpmgTransliterate
                  value={inputValue}
                  onChangeText={handleTextChange}
                  lang={selectedLanguageData?.code || "en"}
                  id="react-transliterate-input"
                  type="textarea"
                  title={inputPlaceholder || t("inputPlaceholder")}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onBlur={() => (isTypingRef.current = false)}
                  placeholder={inputPlaceholder || t("inputPlaceholder")}
                  onKeyDown={onKeyDown}
                  className={`w-full bg-transparent outline-none px-2 sm:px-7  py-2 text-gray-800 dark:text-white text-base resize-none leading-relaxed
                                          placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-200
                                          
                                          ${
                                            isInputDisabledUI
                                              ? "bg-gray-100 dark:bg-gray-600 cursor-not-allowed opacity-50"
                                              : ""
                                          }
                                          ${
                                            isVoiceFlow
                                              ? "placeholder-blue-600"
                                              : ""
                                          }`}
                  autoComplete="off"
                  disabled={isInputDisabledUI}
                  enabled={shouldShowTransliterate}
                  containerStyles={{ color: "#FE7000" }}
                  readOnly={isRecording}
                />
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="relative flex items-center justify-center mr-2">
            <AnimatePresence>
              {!isSendButtonDisabledUI && inputValue?.trim().length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.2 }}
                  className="
                    absolute
                    -top-10
                    right-0
                    bg-[#FF6900]
                    text-white
                    text-xs
                    px-3
                    py-1
                    rounded-md
                    shadow-lg
                    whitespace-nowrap
                    pointer-events-none
                    z-50
                  "
                >
                  {t("send")}
                </motion.div>
              )}
            </AnimatePresence>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
              onClick={handleSend}
              title="Send"
              disabled={isSendButtonDisabledUI}
              className="bg-gradient-to-r from-orange-400 to-orange-600 hover:from-orange-400 hover:to-orange-700
             text-white rounded-full transition-all duration-300
             hover:shadow-lg hover:shadow-orange-500/25
             disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed
             flex items-center justify-center w-[50px] h-[50px] relative overflow-hidden group"
            >
              <motion.div
                animate={{
                  x: isSendButtonDisabledUI ? 0 : [-100, 100],
                }}
                transition={{
                  duration: 2,
                  repeat: isSendButtonDisabledUI ? 0 : Infinity,
                  ease: "easeInOut",
                }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 opacity-0 group-hover:opacity-100"
              />
              <div className="flex items-center space-x-2 relative z-10">
                <motion.div
                  animate={{
                    x: isSendButtonDisabledUI ? 0 : [0, 3, 0],
                    rotate: isSendButtonDisabledUI ? 0 : [0, 15, -5, 0],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: isSendButtonDisabledUI ? 0 : Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <Send size={18} />
                </motion.div>
              </div>
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }
);
UserInputBarWithSendOnly.propTypes = {
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  handleSend: PropTypes.func.isRequired,
  isTypingRef: PropTypes.shape({
    current: PropTypes.bool,
  }).isRequired,
};
UserInputBarWithSendOnly.displayName = "UserInputBarWithSendOnly";
export default UserInputBarWithSendOnly;
